import useSWR from "swr";

import { fetchLeaderboard } from "lib/indexer/client";
import { bigNumberify } from "lib/numbers";
import { CONFIG_UPDATE_INTERVAL } from "lib/timeConstants";

import { LeaderboardDataType } from "./types";

export * from "./types";

export type LeaderboardAccountBase = {
  account: string;
  cumsumCollateral: bigint;
  cumsumSize: bigint;
  sumMaxSize: bigint;

  maxCapital: bigint;
  netCapital: bigint;
  hasRank: boolean;

  realizedPriceImpact: bigint;
  realizedFees: bigint;
  realizedPnl: bigint;

  startUnrealizedPnl: bigint;
  startUnrealizedPriceImpact: bigint;
  startUnrealizedFees: bigint;

  closedCount: number;
  volume: bigint;
  losses: number;
  wins: number;
};

export type LeaderboardAccount = LeaderboardAccountBase & {
  totalCount: number;
  totalPnl: bigint;
  totalQualifyingPnl: bigint;
  totalFees: bigint;
  unrealizedFees: bigint;
  unrealizedPnl: bigint;
  pnlPercentage: bigint;
  averageSize: bigint;
  averageLeverage: bigint;
};

export type LeaderboardPositionBase = {
  key: string;
  account: string;
  realizedFees: bigint;
  unrealizedFees: bigint;
  isLong: boolean;
  market: string;
  maxSize: bigint;
  realizedPriceImpact: bigint;
  unrealizedPriceImpact: bigint;
  isSnapshot: boolean;
  unrealizedPnl: bigint;
  realizedPnl: bigint;
  sizeInTokens: bigint;
  sizeInUsd: bigint;
  entryPrice: bigint;
  collateralToken: string;
  collateralAmount: bigint;
  snapshotTimestamp: number;
};

export type LeaderboardPosition = LeaderboardPositionBase & {
  rank: number;
  fees: bigint;
  pnl: bigint;
  qualifyingPnl: bigint;
  leverage: bigint;
  collateralUsd: bigint;
  closingFeeUsd: bigint;
};

function mapLeaderboardRow(row: any, isAllTime: boolean): LeaderboardAccountBase {
  return {
    account: row.account,
    cumsumCollateral: 0n,
    cumsumSize: 0n,
    sumMaxSize: 0n,
    maxCapital: 0n,
    netCapital: 0n,
    hasRank: row.hasRank ?? true,
    realizedPriceImpact: 0n,
    realizedFees: 0n,
    realizedPnl: bigNumberify(isAllTime ? row.pnl_alltime : row.pnl_30d) ?? 0n,
    startUnrealizedPnl: 0n,
    startUnrealizedPriceImpact: 0n,
    startUnrealizedFees: 0n,
    closedCount: isAllTime ? (row.trades_alltime ?? 0) : (row.trades_30d ?? 0),
    volume: bigNumberify(isAllTime ? row.volume_alltime : row.volume_30d) ?? 0n,
    losses: row.losses_30d ?? 0,
    wins: row.wins_30d ?? 0,
  };
}

export function useLeaderboardData(
  enabled: boolean,
  chainId: number,
  p: {
    account: string | undefined;
    from: number;
    to: number | undefined;
    positionsSnapshotTimestamp: number | undefined;
    leaderboardDataType: LeaderboardDataType | undefined;
  }
) {
  const { data, error, isLoading } = useSWR(
    enabled
      ? [
          "leaderboard/useLeaderboardData",
          chainId,
          p.account,
          p.from,
          p.to,
          p.positionsSnapshotTimestamp,
          p.leaderboardDataType,
        ]
      : null,
    async () => {
      // V1 indexer has no position snapshots — only account-level leaderboard
      if (p.leaderboardDataType === "positions") {
        return { accounts: [], positions: [] as LeaderboardPositionBase[] };
      }

      const isAllTime = !p.from || p.from === 0;
      const sort = isAllTime ? "pnl_alltime" : "pnl_30d";

      const res = await fetchLeaderboard({ sort, limit: 500, account: p.account });

      const allAccountsSet = new Set(res.items.map((r: any) => r.account as string));
      const rows: any[] = [...res.items];

      if (p.account && !allAccountsSet.has(p.account.toLowerCase()) && res.accountRow) {
        rows.push({ ...res.accountRow, hasRank: false });
      }

      return {
        accounts: rows.map((row) => mapLeaderboardRow(row, isAllTime)),
        positions: [] as LeaderboardPositionBase[],
      };
    },
    { refreshInterval: CONFIG_UPDATE_INTERVAL }
  );

  return { data, error, isLoading };
}
